import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { Component, Inject, OnInit, PLATFORM_ID } from "@angular/core";
import {
    makeStateKey,
    Meta,
    Title,
    TransferState,
} from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";

import { BlogService } from "../../shared/blog.service";
import { Blog, BlogAuthor } from "../../shared/model/blogs";

declare var twttr: any;
declare var instgrm: any;

const BLOGPOST = makeStateKey("blogPost");

@Component({
    selector: "mlp-blog-post",
    templateUrl: "./blog-post.component.html",
    styleUrls: ["./blog-post.component.scss"],
})
export class BlogPostComponent implements OnInit {
    blogPost: Blog;
    blogPostAuthor: BlogAuthor;
    notFound = false;
    private isBrowser: boolean = isPlatformBrowser(this.platformId);

    constructor(
        @Inject(PLATFORM_ID) private platformId,
        @Inject(DOCUMENT) private document,
        public blogService: BlogService,
        private activatedRoute: ActivatedRoute,
        private transferState: TransferState,
        private meta: Meta,
        private title: Title
    ) {
        // tslint:disable-next-line:no-object-literal-type-assertion
        const BLOGPOSTDATA = this.transferState.get(BLOGPOST, {} as Blog);

        if (BLOGPOSTDATA && BLOGPOSTDATA.title) this.blogPost = BLOGPOSTDATA;
    }

    ngOnInit(): void {
        if (this.isBrowser) {
            this.embedInstagramCode();
            this.embedTwitterCode();
        }

        this.activatedRoute.params.subscribe(() => {
            this.getBlogPost();
        });
    }

    protected embedInstagramCode(): void {
        const headTag = this.document.getElementsByTagName("head")[0];
        const scriptCode = this.document.createElement("script");
        scriptCode.type = "text/javascript";
        scriptCode.src = "//platform.instagram.com/en_US/embeds.js";
        scriptCode.onload = () => {
            if (typeof instgrm !== "undefined" && instgrm && instgrm.Embeds) {
                instgrm.Embeds.process();
            }
        };
        headTag.appendChild(scriptCode);
    }

    protected embedTwitterCode(): void {
        const headTag = this.document.getElementsByTagName("head")[0];
        const scriptCode = this.document.createElement("script");
        scriptCode.type = "text/javascript";
        scriptCode.src = "//platform.twitter.com/widgets.js";
        scriptCode.async = true;
        headTag.appendChild(scriptCode);
    }

    private getBlogPost(): void {
        const slug = this.activatedRoute.snapshot.params.slug;

        this.notFound = false;
        this.blogService.loading = true;

        const isPreview = this.activatedRoute.snapshot.queryParams.preview;

        if (isPreview) {
            const previewId =
                this.activatedRoute.snapshot.queryParams.preview_id;
            const wpNonce = this.activatedRoute.snapshot.queryParams.wp_rest;

            this.blogService
                .getBlogPostFromPreview(Number(previewId), wpNonce)
                .subscribe((data) => {
                    this.processBlogPost(data);
                });
        } else {
            this.blogService.getBlogPostFromSlug(slug).subscribe((data) => {
                this.processBlogPost(data);
            });
        }
    }

    private processBlogPost(data): void {
        if (data.length > 0) {
            this.blogPost = data[0];
            this.blogService.loading = false;
            this.updateMetaData(this.blogPost);

            this.blogService
                .getBlogAuthorFromId(this.blogPost.author)
                .subscribe((author) => {
                    this.blogPostAuthor = author;
                });

            if (this.isBrowser) {
                window.scrollTo(0, 0);

                // init instagram and twitter after post content is loaded
                if (
                    typeof instgrm !== "undefined" &&
                    instgrm &&
                    instgrm.Embeds
                ) {
                    instgrm.Embeds.process();
                }

                if (typeof twttr !== "undefined") {
                    setTimeout(() => {
                        twttr.widgets.load();
                    }, 1000);
                }
            }

            this.transferState.set(
                BLOGPOST,
                this.blogService.stripOutScriptTags(this.blogPost)
            );
        } else {
            this.notFound = true;
        }
    }

    private updateMetaData(blog: Blog): void {
        const title = `${blog.title} | Playfinder Blog`;
        const metaDescription = blog.meta_description;

        if (this.title.getTitle() !== title) {
            this.title.setTitle(title);
            this.meta.updateTag({ property: "og:title", content: title });
            this.meta.updateTag({ property: "twitter:title", content: title });
        }

        this.meta.updateTag({ name: "description", content: metaDescription });
        this.meta.updateTag({
            property: "og:description",
            content: metaDescription,
        });
        this.meta.updateTag({ property: "og:url", content: blog.link });

        if (blog.featured_image) {
            this.meta.updateTag({
                property: "og:image",
                content: blog.featured_image,
            });
            this.meta.updateTag({
                property: "twitter:image",
                content: blog.featured_image,
            });
        }
    }
}
